import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

const initialState = {
  topics: [],
  questions: [],
  userProgress: {},
  studySessions: [],
  currentSession: null,
  isLoading: false,
  error: null
};

const actionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_QUESTIONS: 'SET_QUESTIONS',
  SET_TOPICS: 'SET_TOPICS',
  ADD_QUESTIONS: 'ADD_QUESTIONS',
  UPDATE_USER_PROGRESS: 'UPDATE_USER_PROGRESS',
  START_SESSION: 'START_SESSION',
  END_SESSION: 'END_SESSION',
  RESET_STATE: 'RESET_STATE'
};

function studyReducer(state, action) {
  switch (action.type) {
    case actionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload
      };
    
    case actionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };
    
    case actionTypes.SET_QUESTIONS:
      return {
        ...state,
        questions: action.payload,
        isLoading: false,
        error: null
      };
    
    case actionTypes.ADD_QUESTIONS:
      const existingIds = new Set(state.questions.map(q => q.id));
      const newQuestions = action.payload.filter(q => !existingIds.has(q.id));
      return {
        ...state,
        questions: [...state.questions, ...newQuestions],
        isLoading: false,
        error: null
      };
    
    case actionTypes.SET_TOPICS:
      return {
        ...state,
        topics: action.payload,
        isLoading: false,
        error: null
      };
    
    case actionTypes.UPDATE_USER_PROGRESS:
      return {
        ...state,
        userProgress: { ...state.userProgress, ...action.payload }
      };
    
    case actionTypes.START_SESSION:
      return {
        ...state,
        currentSession: action.payload
      };
    
    case actionTypes.END_SESSION:
      return {
        ...state,
        studySessions: [...state.studySessions, action.payload],
        currentSession: null
      };
    
    case actionTypes.RESET_STATE:
      return initialState;
    
    default:
      return state;
  }
}

const StudyContext = createContext();

export function StudyProvider({ children }) {
  const [state, dispatch] = useReducer(studyReducer, initialState);
  const { user, loading: authLoading } = useAuth();

  useEffect(() => {
    let isActive = true;

    const loadInitialData = async () => {
      if (authLoading) {
        return;
      }

      const token = localStorage.getItem('study-app-token');
      if (!user || !token) {
        if (isActive) {
          dispatch({ type: actionTypes.RESET_STATE });
        }
        return;
      }

      try {
        dispatch({ type: actionTypes.SET_LOADING, payload: true });

        const topicsData = await apiService.getTopics();
        if (!isActive) {
          return;
        }
        dispatch({ type: actionTypes.SET_TOPICS, payload: topicsData });

        const allQuestions = [];
        for (const topic of topicsData) {
          try {
            const questions = await apiService.getQuestions(topic.api_topic);
            if (!isActive) {
              return;
            }
            allQuestions.push(...questions);
          } catch (error) {
            console.error(`Error loading questions for topic ${topic.name}:`, error);
          }
        }

        if (!isActive) {
          return;
        }
        dispatch({ type: actionTypes.SET_QUESTIONS, payload: allQuestions });
      } catch (error) {
        if (isActive) {
          dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
        }
      }
    };

    loadInitialData();

    return () => {
      isActive = false;
    };
  }, [authLoading, user]);

  const getQuestionsByFilters = async (topicIds, masteryFilters, limit) => {
    try {
      let filteredQuestions = [...state.questions];
      
      if (topicIds && topicIds.length > 0) {
        const selectedTopics = state.topics.filter(topic => topicIds.includes(topic.id));
        const topicNames = selectedTopics.map(topic => topic.api_topic);
        
        filteredQuestions = filteredQuestions.filter(q => 
          topicNames.includes(q.topic)
        );
        
        if (filteredQuestions.length === 0) {
          for (const topicName of topicNames) {
            try {
              const questions = await apiService.getQuestions(topicName);
              if (questions.length > 0) {
                dispatch({ type: actionTypes.ADD_QUESTIONS, payload: questions });
                filteredQuestions.push(...questions);
              }
            } catch (error) {
              console.error(`Error fetching questions for ${topicName}:`, error);
            }
          }
        }
      }
      
      const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
      return limit ? shuffled.slice(0, limit) : shuffled;
    } catch (error) {
      console.error('Error filtering questions:', error);
      return [];
    }
  };

const getTopicStats = () => {
  const stats = {};
  
  if (!state.topics || !Array.isArray(state.topics)) {
    return stats; 
  }
  
  state.topics.forEach(topic => {
    stats[topic.id] = {
      id: topic.id,
      name: topic.name,
      total: state.questions.filter(q => q.topic === topic.api_topic).length,
      answered: 0,
      correct: 0,
      percentage: 0,
      averageTime: 0
    };
  });
  
  return stats;
};

  const getOverallStats = () => {
    return {
      totalQuestions: state.questions.length,
      answeredQuestions: 0,
      correctAnswers: 0,
      accuracy: 0,
      averageTime: 0
    };
  };

  const submitAnswer = async (questionId, answerIndex, isCorrect, timeSpent, topicId, confidenceLevel, sessionId) => {
    try {
      const answerLetter = ['a', 'b', 'c', 'd'][answerIndex] || 'a';
      
      const result = await apiService.submitAnswer(questionId, answerLetter, confidenceLevel, timeSpent, sessionId);
      
      dispatch({
        type: actionTypes.UPDATE_USER_PROGRESS,
        payload: {
          [questionId]: {
            answered: true,
            isCorrect,
            timeSpent,
            topicId,
            answeredAt: new Date().toISOString(),
            confidenceLevel
          }
        }
      });
      
      return result;
    } catch (error) {
      console.error('Error submitting answer:', error);
      throw error;
    }
  };

  const refreshQuestions = async () => {
    try {
      dispatch({ type: actionTypes.SET_LOADING, payload: true });
      
      const topicsData = await apiService.getTopics();
      dispatch({ type: actionTypes.SET_TOPICS, payload: topicsData });
      
      const allQuestions = [];
      for (const topic of topicsData) {
        try {
          const questions = await apiService.getQuestions(topic.api_topic);
          allQuestions.push(...questions);
        } catch (error) {
          console.error(`Error loading questions for topic ${topic.name}:`, error);
        }
      }
      
      dispatch({ type: actionTypes.SET_QUESTIONS, payload: allQuestions });
    } catch (error) {
      dispatch({ type: actionTypes.SET_ERROR, payload: error.message });
      throw error;
    }
  };

  const resetStudyState = () => {
    dispatch({ type: actionTypes.RESET_STATE });
  };

  const value = {
    ...state,
    dispatch,
    getQuestionsByFilters,
    getTopicStats,
    getOverallStats,
    submitAnswer,
    refreshQuestions,
    resetStudyState,
    actionTypes
  };

  return (
    <StudyContext.Provider value={value}>
      {children}
    </StudyContext.Provider>
  );
}

export function useStudy() {
  const context = useContext(StudyContext);
  if (!context) {
    throw new Error('useStudy must be used within a StudyProvider');
  }
  return context;
}
